import { AfterViewChecked, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, NgZone, ChangeDetectionStrategy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services/auth.service';
import { TicketService } from '@services/ticket.service';
import { LoaderService } from '@services/support/loader.service';
import { SnackbarService } from '@services/support/snackbar.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { QuillEditorComponent } from 'ngx-quill';
import "quill-mention";
import Quill from 'quill';
import { MentionBlot, Mention } from 'quill-mention';
import { AlertModalComponent } from 'src/app/components/modals/alert-modal/alert-modal.component';

const CUSTOM_BLOT_NAME = 'styled-mention';
class StyledMentionBlot extends MentionBlot {
  static override create(data) {
    const element = document.createElement('a');
    element.className = 'mention';
    element.innerText = data.name;
    element.setAttribute('data-id', data.id);
    return element;
  }
}
StyledMentionBlot.blotName = CUSTOM_BLOT_NAME;

// Register only if not already registered
if (!Quill.imports['blots/styled-mention']) {
  Quill.register({ 'blots/styled-mention': StyledMentionBlot });
}

export interface TimelineItem {
  type: 'chat' | 'session' | 'file';
  timestamp: number;
  data: any;
}

@Component({
    selector: 'app-ticket-chat-timeline',
    templateUrl: './ticket-chat-timeline.component.html',
    styleUrls: ['./ticket-chat-timeline.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TicketChatTimelineComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked {
  @Input() ticketId: string;
  @Input() sessions: any[] = [];
  @Input() files: any[] = [];
  @Input() users: any[] = [];
  @Output() navigateToTab = new EventEmitter<number>();
  @Output() sessionSelected = new EventEmitter<string>();
  @Output() fileUploaded = new EventEmitter<void>();

  @ViewChild(QuillEditorComponent) quillRef!: QuillEditorComponent;
  @ViewChild('timelineArea') timelineAreaRef: ElementRef;

  timelineItems: TimelineItem[] = [];
  chatMessages: any[] = [];
  currentUserId: string;
  commentText: string = '';
  hasContent: boolean = false;
  isCommentSending = false;
  imageExtensions: string[] = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'];
  private shouldScrollToBottom = false;

  private subscriptions: Subscription[] = [];
  private chatSub: Subscription | null = null;
  private readonly systemMessagePrefix = '__system__:';

  modules: any = {};

  constructor(
    private ticketService: TicketService,
    private authService: AuthService,
    private sanitizer: DomSanitizer,
    private loaderService: LoaderService,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
    private zone: NgZone,
    private modal: BsModalService
  ) {}

  get editor() {
    return this.quillRef?.quillEditor;
  }

  ngOnInit(): void {
    this.currentUserId = this.authService.currentUser?.id;
    this.initModules();
    this.loadChatMessages();
  }

  private initModules(): void {
    this.modules = {
      toolbar: false,
      keyboard: {
        bindings: {
          enter: {
            key: 13,
            shiftKey: false,
            handler: () => {
              if (this.hasContent && !this.isCommentSending) {
                this.sendComment();
              }
              return false; // Prevent default enter behavior
            }
          }
        }
      },
      mention: {
        allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
        spaceAfterInsert: true,
        mentionDenotationChars: ["@"],
        dataAttributes: ['id', 'name'],
        blotName: CUSTOM_BLOT_NAME,
        renderItem: (data) => data.name,
        source: (searchTerm, renderList) => {
          if (searchTerm.length === 0) {
            renderList(this.users.map(u => ({ id: u.id, name: u.name, value: u.name })), searchTerm);
          } else {
            const matches = this.users
              .filter(u => u.name.toLowerCase().includes(searchTerm.toLowerCase()))
              .map(u => ({ id: u.id, name: u.name, value: u.name }));
            renderList(matches, searchTerm);
          }
        }
      }
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ticketId'] && this.ticketId) {
      this.loadChatMessages();
    }
    if (changes['sessions'] || changes['files']) {
      this.buildTimeline();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.chatSub?.unsubscribe();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  private scrollToBottom(): void {
    try {
      if (this.timelineAreaRef?.nativeElement) {
        this.timelineAreaRef.nativeElement.scrollTop = this.timelineAreaRef.nativeElement.scrollHeight;
      }
    } catch (err) {}
  }

  private loadChatMessages(): void {
    if (!this.ticketId) return;
    
    this.chatSub?.unsubscribe();
    this.chatSub = this.ticketService.getTicketData(this.ticketId).subscribe(
      ticketData => {
        // Firebase stores push() data as object with keys, convert to array
        const chatObj = ticketData?.chat || {};
        if (typeof chatObj === 'object' && !Array.isArray(chatObj)) {
          this.chatMessages = Object.keys(chatObj).map(key => ({
            id: key,
            ...chatObj[key]
          }));
        } else if (Array.isArray(chatObj)) {
          this.chatMessages = chatObj;
        } else {
          this.chatMessages = [];
        }
        this.buildTimeline();
      },
      error => {
        // Silence permission errors during logout
        const errorMsg = error?.message || error?.toString() || '';
        if (!errorMsg.toLowerCase().includes('permission')) {
          console.error('Error loading chat messages:', error);
        }
      }
    );
  }

  private buildTimeline(): void {
    const items: TimelineItem[] = [];

    // Add chat messages
    if (this.chatMessages && Array.isArray(this.chatMessages)) {
      this.chatMessages.forEach(msg => {
        items.push({
          type: 'chat',
          timestamp: msg.timestamp || msg.createdAt || 0,
          data: msg
        });
      });
    }

    // Add sessions
    if (this.sessions && Array.isArray(this.sessions)) {
      this.sessions.forEach(session => {
        items.push({
          type: 'session',
          timestamp: session.create_time || 0,
          data: session
        });
      });
    }

    // Add files
    if (this.files && Array.isArray(this.files)) {
      this.files.forEach(file => {
        const fileTimestamp = file.create_time || file.upload_time || file.timestamp || 0;
        items.push({
          type: 'file',
          timestamp: fileTimestamp,
          data: file
        });
      });
    }

    // Sort by timestamp ascending (oldest first)
    items.sort((a, b) => a.timestamp - b.timestamp);
    this.timelineItems = items;
    this.shouldScrollToBottom = true;
  }

  getContent(event): void {
    this.zone.run(() => {
      this.hasContent = this.editor?.getText()?.trim()?.length > 0;
    });
  }

  onEditorCreated(quill: any): void {
    // Use capture phase to intercept before Quill handles it
    quill.root.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.zone.run(() => {
          if (this.hasContent && !this.isCommentSending) {
            this.sendComment();
          }
        });
      }
    }, true); // capture phase

    quill.root.addEventListener('paste', (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items || items.length === 0) {
        return;
      }

      const pasteFiles: File[] = [];
      Array.from(items).forEach(item => {
        if (item.kind === 'file' && (item.type.startsWith('image/') || item.type === 'application/pdf')) {
          const file = item.getAsFile();
          if (file) {
            pasteFiles.push(file);
          }
        }
      });


      if (pasteFiles.length === 0) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      this.zone.run(() => {
        const initialState = {
          headerTitle: this.translateService.instant('MAIN.CONTENT.TICKETS.CHAT_TIMELINE.PASTE_UPLOAD_TITLE'),
          textMessage: this.translateService.instant('MAIN.CONTENT.TICKETS.CHAT_TIMELINE.PASTE_UPLOAD_CONFIRM'),
          confirmButtonText: this.translateService.instant('MAIN.CONTENT.TICKETS.CHAT_TIMELINE.PASTE_UPLOAD_CONFIRM_BUTTON'),
          confirmButtonStyle: 'primary'
        };
        const alertmodalRef = this.modal.show(AlertModalComponent, {
          initialState,
          backdrop: 'static',
          class: 'modal-dialog-centered',
          animated: false
        });
        alertmodalRef.content.onClose.subscribe((res: boolean) => {
          if (res) {
            this.uploadFiles(pasteFiles);
          } else {
            alertmodalRef.hide();
          }
        });
      });
    }, true);

    quill.root.addEventListener('dragover', (event: DragEvent) => {
      if (!event.dataTransfer) {
        return;
      }
      const files = this.collectTransferFiles(event.dataTransfer);
      if (files.length > 0) {
        event.preventDefault();
      }
    }, true);

    quill.root.addEventListener('drop', (event: DragEvent) => {
      if (!event.dataTransfer) {
        return;
      }

      const dropFiles = this.collectTransferFiles(event.dataTransfer);
      if (dropFiles.length === 0) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      this.zone.run(() => {
        const initialState = {
          headerTitle: this.translateService.instant('MAIN.CONTENT.TICKETS.CHAT_TIMELINE.PASTE_UPLOAD_TITLE'),
          textMessage: this.translateService.instant('MAIN.CONTENT.TICKETS.CHAT_TIMELINE.PASTE_UPLOAD_CONFIRM'),
          confirmButtonText: this.translateService.instant('MAIN.CONTENT.TICKETS.CHAT_TIMELINE.PASTE_UPLOAD_CONFIRM_BUTTON'),
          confirmButtonStyle: 'primary'
        };
        const alertmodalRef = this.modal.show(AlertModalComponent, {
          initialState,
          backdrop: 'static',
          class: 'modal-dialog-centered',
          animated: false
        });
        alertmodalRef.content.onClose.subscribe((res: boolean) => {
          if (res) {
            this.uploadFiles(dropFiles);
          } else {
            alertmodalRef.hide();
          }
        });
      });
    }, true);
  }

  byPassHTML(html: any): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  sendComment(): void {
    const textContent = this.editor?.getText()?.trim();
    if (!textContent) return;

    this.isCommentSending = true;
    
    const chatMessage = {
      text: this.commentText,
      userId: this.currentUserId,
      senderName: this.authService.currentUser?.name || '',
      timestamp: Date.now()
    };

    this.ticketService.addTicketChatMessage(this.ticketId, chatMessage)
      .then(() => {
        this.commentText = '';
        this.hasContent = false;
        this.isCommentSending = false;
      })
      .catch(err => {
        console.error('Error sending comment:', err);
        this.snackbarService.error(this.translateService.instant('MAIN.CONTENT.TICKETS.CHAT_TIMELINE.SEND_ERROR'));
        this.isCommentSending = false;
      });
  }

  chooseButtonUpload(event: any): void {
    const files = event.target.files ? Array.from(event.target.files as FileList) : [];
    this.uploadFiles(files);
  }

  private uploadFiles(files: File[]): void {
    if (!files || files.length === 0) {
      return;
    }

    this.loaderService.show();
    const formData = new FormData();
    files.forEach(file => formData.append(file.name, file));

    this.ticketService.uploadTicketFiles(formData, this.ticketId, this.authService.currentUser.token)
      .then(() => {
        return new Promise(resolve => setTimeout(resolve, 500));
      })
      .then(() => {
        this.loaderService.hide();
        this.fileUploaded.emit();
        this.snackbarService.success(this.translateService.instant('MAIN.CONTENT.TICKETS.FILES.UPLOAD_SUCCESS'));
      })
      .catch(err => {
        this.loaderService.hide();
        console.error('Error uploading file:', err);
        this.snackbarService.error(this.translateService.instant('MAIN.CONTENT.TICKETS.FILES.UPLOAD_ERROR'));
      });
  }

  private collectTransferFiles(dataTransfer: DataTransfer): File[] {
    const files: File[] = [];

    if (dataTransfer.items && dataTransfer.items.length > 0) {
      Array.from(dataTransfer.items).forEach(item => {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file && (file.type.startsWith('image/') || file.type === 'application/pdf')) {
            files.push(file);
          }
        }
      });
      return files;
    }

    if (dataTransfer.files && dataTransfer.files.length > 0) {
      Array.from(dataTransfer.files).forEach(file => {
        if (file.type.startsWith('image/') || file.type === 'application/pdf') {
          files.push(file);
        }
      });
    }

    return files;
  }

  getUsersName(uid: string): string {
    if (!uid) return '';
    const user = this.users.find(u => u.id === uid);
    return user ? user.name : uid;
  }

  formatTimestamp(timestamp: number): string {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString();
  }

  formatDate(timestamp: number): string {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString();
  }

  formatDateDivider(timestamp: number): string {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (this.isSameDay(date, today)) {
      return this.translateService.instant('MAIN.CONTENT.TICKETS.CHAT_TIMELINE.TODAY');
    } else if (this.isSameDay(date, yesterday)) {
      return this.translateService.instant('MAIN.CONTENT.TICKETS.CHAT_TIMELINE.YESTERDAY');
    } else {
      return date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
    }
  }

  shouldShowDateDivider(item: TimelineItem, index: number): boolean {
    if (index === 0) return true;
    const prevItem = this.timelineItems[index - 1];
    const currentDate = new Date(item.timestamp);
    const prevDate = new Date(prevItem.timestamp);
    return !this.isSameDay(currentDate, prevDate);
  }

  private isSameDay(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }

  formatTime(timestamp: number): string {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  isImageFile(file: any): boolean {
    if (!file) return false;
    const ext = (file.extension || file.name?.split('.').pop() || '').toLowerCase();
    return this.imageExtensions.includes(ext);
  }

  onSessionClick(session: any): void {
    if (session?.id) {
      this.sessionSelected.emit(session.id);
    }
    this.navigateToTab.emit(2); // Navigate to Attached Sessions tab
  }

  onFileClick(file: any): void {
    this.navigateToTab.emit(3); // Navigate to Files tab
  }

  getDuration(startTime: number, endTime: number): number {
    if (!startTime || !endTime) return 0;
    return Math.ceil((endTime - startTime) / 60000);
  }

  isOwnMessage(item: TimelineItem): boolean {
    return item.type === 'chat' && !this.isSystemMessage(item) && item.data?.userId === this.currentUserId;
  }

  isSystemMessage(item: TimelineItem): boolean {
    return item.type === 'chat' && this.isSystemText(item?.data?.text);
  }

  getSystemMessageText(item: TimelineItem): string {
    const parsed = this.parseSystemMessage(item?.data?.text);
    if (!parsed) {
      return String(item?.data?.text || '');
    }
    const params = this.formatSystemParams(parsed.params || {}, item?.data?.userId, item?.data?.senderName);
    return this.translateService.instant(parsed.key, params);
  }

  private isSystemText(text: string): boolean {
    return typeof text === 'string' && text.startsWith(this.systemMessagePrefix);
  }

  private parseSystemMessage(text: string): { key: string; params: any } | null {
    if (!this.isSystemText(text)) {
      return null;
    }
    try {
      const payload = JSON.parse(text.replace(this.systemMessagePrefix, ''));
      if (!payload?.key) {
        return null;
      }
      return { key: payload.key, params: payload.params || {} };
    } catch (error) {
      return null;
    }
  }

  private formatSystemParams(params: any, userId?: string, senderName?: string): any {
    const field = params?.field;
    const formatted: any = { ...params };
    const fallbackActor = this.translateService.instant('MAIN.CONTENT.TICKETS.CHAT_TIMELINE.SYSTEM.DEFAULT_ACTOR');
    formatted.actor = params?.actor || senderName || this.getUsersName(userId) || fallbackActor;

    if (!field) {
      return formatted;
    }

    formatted.from = this.formatSystemValue(field, params?.from, params?.fromLabel);
    formatted.to = this.formatSystemValue(field, params?.to, params?.toLabel);
    return formatted;
  }

  private formatSystemValue(field: string, value: any, label?: string): string {
    if (label) {
      return label;
    }
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (field === 'status') {
      const key = `MAIN.CONTENT.TICKETS.STATUS_${String(value).toUpperCase().replace('-', '_')}`;
      return this.translateService.instant(key);
    }

    if (field === 'priority') {
      const key = `MAIN.CONTENT.TICKETS.PRIORITY_${String(value).toUpperCase()}`;
      return this.translateService.instant(key);
    }

    if (field === 'assignee') {
      return this.getUsersName(String(value));
    }

    if (field === 'followup') {
      const dateValue = new Date(value);
      if (Number.isNaN(dateValue.getTime())) {
        return String(value);
      }
      return this.formatDate(dateValue.getTime());
    }

    return String(value);
  }
}
